import { lazy } from "react";
import type { ComponentType } from "react";

const SESSION_KEY = "chunk-load-retry";

/**
 * React.lazy のラッパー。チャンク読み込み失敗時にページをリロードして再取得する。
 *
 * デプロイ時にJSチャンクのハッシュが変わると、旧チャンクがS3から削除され、
 * キャッシュされた旧 index.html を持つブラウザが存在しないチャンクを要求してしまう。
 * この関数は読み込み失敗を検知して1回だけリロードし、新しい index.html を取得する。
 * 無限リロードを防ぐため sessionStorage でリトライ済みフラグを管理する。
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazyWithRetry = (importFn: () => Promise<{ default: ComponentType<any> }>) => {
  return lazy(async () => {
    const hasRetried = sessionStorage.getItem(SESSION_KEY) === "true";

    try {
      const component = await importFn();
      sessionStorage.removeItem(SESSION_KEY);
      return component;
    } catch (error) {
      if (!hasRetried) {
        sessionStorage.setItem(SESSION_KEY, "true");
        window.location.reload();
        // リロード中にReactがレンダリングしないよう、resolveしないPromiseを返す
        return new Promise(() => {});
      }
      // リトライ済みなら元のエラーをそのまま投げる
      throw error;
    }
  });
};

export default lazyWithRetry;
